// API Configuration and Base Service
import { API_CONFIG, ENV, FEATURES } from '../config/env';

const API_BASE_URL = API_CONFIG.BASE_URL;

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // Get auth headers
  getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  // Get admin auth headers
  getAdminAuthHeaders() {
    const token = localStorage.getItem('adminAuthToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  // Generic request method
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    
    // Don't send auth headers for public endpoints
    const isPublicEndpoint = endpoint.includes('/login/') || 
                           endpoint.includes('/register/') || 
                           endpoint.includes('/send-mobile-otp/') ||
                           endpoint.includes('/verify-mobile-otp/') ||
                           endpoint.includes('/send-email-otp/') ||
                           endpoint.includes('/verify-email-otp/') ||
                           endpoint.includes('/core/content/') ||
                           endpoint.includes('/core/contact/') ||
                           endpoint.includes('/core/newsletter/');
    const headers = isPublicEndpoint ? { 'Content-Type': 'application/json' } : this.getAuthHeaders();
    
    const config = {
      headers,
      ...options
    };

    try {
      const response = await fetch(url, config);
      
      // Check content type before parsing JSON
      const contentType = response.headers.get('content-type') || '';
      let data;
      
      if (contentType.includes('application/json')) {
        try {
          data = await response.json();
        } catch (jsonError) {
          // If JSON parsing fails, read as text
          const text = await response.text();
          const error = new Error(`Invalid JSON response: ${text.substring(0, 200)}`);
          error.status = response.status;
          error.response = { status: response.status, data: text };
          throw error;
        }
      } else {
        // Server returned non-JSON (likely HTML error page)
        const text = await response.text();
        const error = new Error(
          `Server returned ${contentType || 'non-JSON'} response. ` +
          `This usually indicates a server error. Status: ${response.status}`
        );
        error.status = response.status;
        error.response = { status: response.status, data: text };
        error.isHtmlResponse = text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html');
        throw error;
      }

      if (!response.ok) {
        // Handle 401 Unauthorized - but NOT for login/register endpoints
        // Login failures should return 401 but we shouldn't clear tokens or redirect
        const isAuthEndpoint = endpoint.includes('/login/') || 
                              endpoint.includes('/register/') ||
                              endpoint.includes('/send-mobile-otp/') ||
                              endpoint.includes('/verify-mobile-otp/') ||
                              endpoint.includes('/send-email-otp/') ||
                              endpoint.includes('/verify-email-otp/');
        
        if (response.status === 401 && !isAuthEndpoint) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('userData');
          localStorage.removeItem('refreshToken');
          if (window.location.pathname !== '/signin') {
            window.location.href = '/signin';
          }
        }
        
        // Create a more detailed error object
        // For login endpoints, use the error message from the API response
        const errorMessage = data.error || data.detail || `HTTP error! status: ${response.status}`;
        const error = new Error(errorMessage);
        error.status = response.status;
        error.data = data;
        error.response = { data, status: response.status };
        throw error;
      }

      return data;
    } catch (error) {
      // Don't log expected authentication failures (invalid credentials during login)
      const isAuthEndpoint = endpoint.includes('/login/') || 
                            endpoint.includes('/register/') ||
                            endpoint.includes('/send-mobile-otp/') ||
                            endpoint.includes('/verify-mobile-otp/') ||
                            endpoint.includes('/send-email-otp/') ||
                            endpoint.includes('/verify-email-otp/');
      
      throw error;
    }
  }

  // Admin request method
  async adminRequest(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    
    // Don't send auth headers for admin login endpoints
    const isAuthEndpoint = endpoint.includes('/admin/login/');
    const headers = isAuthEndpoint ? { 'Content-Type': 'application/json' } : this.getAdminAuthHeaders();
    
    const config = {
      headers,
      ...options
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      // Don't log expected authentication failures (invalid credentials during admin login)
      const isAuthEndpoint = endpoint.includes('/admin/login/');
      
      throw error;
    }
  }

  // Admin file upload method
  async adminFileUpload(endpoint, formData, method = 'POST') {
    const url = `${this.baseURL}${endpoint}`;
    const token = localStorage.getItem('adminAuthToken');
    
    const config = {
      method: method,
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      throw error;
    }
  }
}

export default new ApiService();
